import type { ProblemDetails } from "@fp/admin-api-client";
import { FsmPlatform } from "@fp/admin-api-client";
import {
  getGetFsmConnectionByIdQueryKey,
  getListFsmConnectionsQueryKey,
  useGetFsmConnectionById,
  useListFsmConnections,
  useTestFsmConnection,
} from "@fp/admin-api-client/fsm-connections/fsm-connections.client";
import {
  getGetColdStandardConnectionQueryKey,
  useCreateColdStandardConnection,
  useGetColdStandardConnection,
  useUpdateColdStandardConnection,
} from "@fp/admin-api-client/cold-standard-connections/cold-standard-connections.client";
import { Button } from "@fp/ui/components/base/buttons/button";
import {
  PageContentContainer,
} from "@fp/ui/components/base/page-layout/page-layout";
import {
  Tooltip,
  TooltipTrigger,
} from "@fp/ui/components/base/tooltip/tooltip";
import { useClipboard } from "@fp/ui/hooks/use-clipboard";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  Copy01,
  Eye,
  EyeOff,
  Zap,
} from "@untitledui/icons";
import type { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { toast } from "@/lib/toast";
import { CompletedWorkCard, type CompletedWorkCardCopy } from "./-components/completed-work-card";
import { ConnectionCardHeader } from "./-components/connection-card-header";
import { platformPresentation } from "./-components/platform-presentation";
import type { PayoutTriggersCopy } from "./-components/payout-triggers-section";
import { SetupChecklist, type SetupChecklistCopy } from "./-components/setup-checklist";
import { TechsCard, type TechsCardCopy } from "./-components/techs-card";
import { SyncIssuesCard } from "./-components/sync-issues-card";
import type { SyncSwitchCopy } from "./-components/sync-switch";
import { useRefreshConnectionOnVisit } from "./-components/use-refresh-connection-on-visit";

export const Route = createFileRoute("/integrations/coldstandard")({
  component: RouteComponent,
});

function ConnectForm() {
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState("");

  const createMutation = useCreateColdStandardConnection({
    mutation: {
      onSuccess: () => {
        toast.success("Cold Standard connected");
        queryClient.invalidateQueries({
          queryKey: getListFsmConnectionsQueryKey(),
        });
      },
      onError: (error: AxiosError<ProblemDetails | undefined>) => {
        const message =
          (error.response?.data as ProblemDetails | undefined)?.title ??
          "Failed to connect";
        toast.error(message);
      },
    },
  });

  const handleConnect = () => {
    if (!apiKey.trim()) {
      toast.error("Please enter your API key");
      return;
    }
    createMutation.mutate({
      data: { apiKey: apiKey.trim() },
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">
        Connect Cold Standard
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        Enter the API key Cold Standard issued for FieldPay to start syncing
        finalized visits and approved reimbursements.
      </p>
      <div className="space-y-4">
        <div>
          <label
            htmlFor="coldstandard-api-key"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            API Key
          </label>
          <input
            id="coldstandard-api-key"
            type="password"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            placeholder="Enter your Cold Standard API key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={createMutation.isPending}
          />
        </div>
        <Button
          color="primary"
          onClick={handleConnect}
          isLoading={createMutation.isPending}
        >
          Connect
        </Button>
      </div>
    </div>
  );
}

function ConnectionCard({ connectionId }: { connectionId: string }) {
  const queryClient = useQueryClient();
  const { copy } = useClipboard();
  const [isApiKeyMasked, setIsApiKeyMasked] = useState(true);
  const [apiKeyValue, setApiKeyValue] = useState("");
  const { data: connectionResponse } = useGetFsmConnectionById(connectionId);
  const connection = connectionResponse?.data;

  const { data: revealResponse } = useGetColdStandardConnection(connectionId, {
    query: {
      queryKey: getGetColdStandardConnectionQueryKey(connectionId),
      staleTime: Number.POSITIVE_INFINITY,
    },
  });
  const apiKey = revealResponse?.data?.apiKey ?? "";

  useEffect(() => {
    if (apiKey) {
      setApiKeyValue(apiKey);
    }
  }, [apiKey]);

  const testMutation = useTestFsmConnection({
    mutation: {
      onSuccess: (response) => {
        if (response.data.isSuccess) {
          toast.success("Connection verified");
        } else {
          toast.error(
            "Cold Standard rejected this API key. Check the key and try again.",
          );
        }
      },
      onError: () => {
        toast.error(
          "Something went wrong testing the connection. Please try again.",
        );
      },
    },
  });

  const updateApiKeyMutation = useUpdateColdStandardConnection({
    mutation: {
      onSuccess: () => {
        toast.success("API key saved");
        setIsApiKeyMasked(true);
        queryClient.invalidateQueries({
          queryKey: getGetFsmConnectionByIdQueryKey(connectionId),
        });
        queryClient.invalidateQueries({
          queryKey: getGetColdStandardConnectionQueryKey(connectionId),
        });
      },
      onError: (error: AxiosError<ProblemDetails | undefined>) => {
        toast.error(
          error.response?.status === 400
            ? "Cold Standard rejected this API key. Check the key and try again."
            : "Something went wrong saving the API key. Please try again.",
        );
      },
    },
  });

  if (!connection) {
    return null;
  }

  const isVerifying = testMutation.isPending || updateApiKeyMutation.isPending;

  const handleTestOrSave = () => {
    const trimmedApiKey = apiKeyValue.trim();
    if (trimmedApiKey === apiKey) {
      testMutation.mutate({ id: connectionId });
    } else {
      updateApiKeyMutation.mutate({
        id: connectionId,
        data: { apiKey: trimmedApiKey },
      });
    }
  };

  const copyApiKey = async () => {
    if (!apiKey) {
      return;
    }
    const { success } = await copy(apiKey);
    if (success) {
      toast.success("API key copied");
    } else {
      toast.error("Failed to copy API key");
    }
  };

  return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <ConnectionCardHeader connectionId={connectionId} platformName="Cold Standard" syncSwitchCopy={COLD_STANDARD_SYNC_SWITCH_COPY} />
        <dt className="text-sm font-medium text-gray-500">API Key</dt>
        <div className="mt-1 flex items-center gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <input
              type={isApiKeyMasked ? "password" : "text"}
              value={apiKeyValue}
              onChange={(e) => setApiKeyValue(e.target.value)}
              aria-label="API Key"
              className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-mono text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <Tooltip
              title={isApiKeyMasked ? "Reveal API key" : "Hide API key"}
              placement="top"
            >
              <TooltipTrigger
                onPress={() => setIsApiKeyMasked((masked) => !masked)}
                aria-label={isApiKeyMasked ? "Reveal API key" : "Hide API key"}
                className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              >
                {isApiKeyMasked ? (
                  <Eye className="size-4" />
                ) : (
                  <EyeOff className="size-4" />
                )}
              </TooltipTrigger>
            </Tooltip>
            <Tooltip title="Copy API key" placement="top">
              <TooltipTrigger
                onPress={copyApiKey}
                aria-label="Copy API key"
                className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              >
                <Copy01 className="size-4" />
              </TooltipTrigger>
            </Tooltip>
          </div>
          <Button
            size="sm"
            color="secondary"
            onClick={handleTestOrSave}
            isLoading={isVerifying}
            isDisabled={!apiKeyValue.trim()}
          >
            <span className="flex items-center gap-1">
              <Zap className="size-4" />
              Test
            </span>
          </Button>
        </div>
      </div>
  );
}

const COLD_STANDARD_COMPLETED_WORK_COPY: CompletedWorkCardCopy = {
  syncWindowHelp:
    "This sets how far back Preview and Sync now look for finalized visits and approved reimbursements. Automatic syncing only picks up visits and reimbursements finalized after sync was turned on — use Sync now to reach further back.",
  previewFoundNothing:
    "Preview found no finalized visits or approved reimbursements in the sync window.",
  previewFoundNothingNew:
    "Preview found no new completed work — everything in the sync window has already been processed.",
  syncNowTitle: "Sync from Cold Standard now",
  syncNowMessage: (days) =>
    `${days === 0 ? "This runs a real sync over today's finalized visits and approved reimbursements." : `This runs a real sync over the last ${days} ${days === 1 ? "day" : "days"} of finalized visits and approved reimbursements.`}`,
  syncFoundNothing:
    "Sync found no finalized visits or approved reimbursements in the sync window.",
  syncFoundNothingNew:
    "Sync found no new completed work — everything in the sync window has already been processed.",
};

const COLD_STANDARD_SYNC_SWITCH_COPY: SyncSwitchCopy = {
  turnOnSyncTitle: "Turn on Cold Standard sync",
  turnOnSyncMessage:
    "Sync imports finalized visits and approved reimbursements that match your payout triggers.",
};

const COLD_STANDARD_PAYOUT_TRIGGERS_COPY: PayoutTriggersCopy = {
  description:
    "Choose which Cold Standard visit categories and reimbursements trigger payouts. Only the selected categories and reimbursements create payout requests.",
  loadFailed: "Failed to load payout triggers from Cold Standard.",
  noneAvailable: "Nothing published by Cold Standard yet.",
  noneSelected: "Nothing selected — no visits or reimbursements will trigger payouts.",
};

const COLD_STANDARD_TECHS_COPY: TechsCardCopy = {
  description:
    "The techs list shows every tech Cold Standard reports and which techs FieldPay can pay. A tech is payable once Cold Standard marks the tech payable and the tech is linked to a talent member who has finished payout setup or to an active organization on your roster. FieldPay reads Cold Standard's decision on every tech sync.",
  autoInviteTooltip:
    "FieldPay emails an invitation to every Cold Standard tech marked payable in Cold Standard who has an email and is not a talent member yet.",
  autoInviteDescription:
    "Invite every Cold Standard tech marked payable in Cold Standard who is not a talent member yet.",
  notPayableReasons: {
    opted_out: {
      label: "Opted out in Cold Standard",
      guidance: "Opt the tech back in within Cold Standard.",
    },
    type_unset: {
      label: "No pay type in Cold Standard",
      guidance: "Set a pay type for the tech in Cold Standard.",
    },
    not_payable_type: {
      label: "Not payable in Cold Standard",
      guidance: "Give the tech a payable pay type in Cold Standard.",
    },
  },
};

const COLD_STANDARD_SETUP_CHECKLIST_COPY: SetupChecklistCopy = {
  payableTechLabel: "Cold Standard marks at least one tech payable",
};

function ConnectedView({ connectionId }: { connectionId: string }) {
  useRefreshConnectionOnVisit(connectionId);

  return (
    <div className="space-y-6">
      <SetupChecklist
        connectionId={connectionId}
        copy={COLD_STANDARD_SETUP_CHECKLIST_COPY}
      />
      <ConnectionCard connectionId={connectionId} />
      <TechsCard
        connectionId={connectionId}
        platformName="Cold Standard"
        copy={COLD_STANDARD_TECHS_COPY}
      />
      <CompletedWorkCard
        connectionId={connectionId}
        platformName="Cold Standard"
        copy={COLD_STANDARD_COMPLETED_WORK_COPY}
        payoutTriggersCopy={COLD_STANDARD_PAYOUT_TRIGGERS_COPY}
      />
      <SyncIssuesCard connectionId={connectionId} />
    </div>
  );
}

function RouteComponent() {
  const { data: connectionsResponse } = useListFsmConnections();
  const connections = connectionsResponse?.data ?? [];
  const coldStandardConnection = connections.find(
    (c) => c.platform === FsmPlatform.ColdStandard,
  );

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Cold Standard"
        parent={{ url: "/integrations", label: "Integrations" }}
        titleIcon={
          <img
            src={platformPresentation[FsmPlatform.ColdStandard].logoUrl}
            alt="Cold Standard"
            className="size-8 rounded-lg object-contain"
          />
        }
      />
      <PageContentContainer>
        {connectionsResponse === undefined ? (
          <div className="text-sm text-gray-500">Loading...</div>
        ) : coldStandardConnection?.isConnected ? (
          <ConnectedView connectionId={coldStandardConnection.id} />
        ) : (
          <ConnectForm />
        )}
      </PageContentContainer>
    </AdminLayout>
  );
}
